import { getUserRole } from '../userLocalDetails';

export const getJeddaiRole = (profile) => {
  return profile?.permissions?.jeddaiRole;
};

export const getUpgradeOptions = (profile) => {
  const options = profile?.permissions?.upgradeOptions || [];
  return options;
};

export const canUpgradeTo = (profile, option) => {
  const options = getUpgradeOptions(profile);
  return options.includes(option);
};

export const getUpgradeOption = (profile) => {
  const options = getUpgradeOptions(profile);
  if (options.length === 0 || options.includes('NONE')) {
    return 'NONE';
  }
  return options[0];
};

export const isTeacherRole = (profile) => {
  const jeddaiRole = getJeddaiRole(profile);
  if (!jeddaiRole) return false;
  const roleUpper = String(jeddaiRole).toUpperCase();
  return roleUpper === 'TEACHER';
};

export const isStudentRole = (profile) => {
  const jeddaiRole = getJeddaiRole(profile);
  return Boolean(jeddaiRole) && String(jeddaiRole).toUpperCase() === 'STUDENT';
};

export const isSchoolStudentRole = (profile) => {
  return isStudentRole(profile) && profile?.permissions?.hasClasses === true;
};

export const isNonSchoolStudentRole = (profile) => {
  return isStudentRole(profile) && profile?.permissions?.hasClasses !== true;
};

export const hasTeacherSku = (profile) => {
  const sku = profile?.subscriptionDetails?.sku || '';
  return String(sku).toUpperCase().includes('JAI-TEA');
};

export const isNonSchoolTeacherRole = (profile) => {
  return isTeacherRole(profile) && profile?.permissions?.hasClasses !== true;
};

export const isExpertRole = (profile) => {
  return (
    isTeacherRole(profile) &&
    profile?.permissions?.hasClasses !== true &&
    !hasTeacherSku(profile)
  );
};

export const isSchoolTeacherRole = (profile)=>{
  return (
    profile?.roles?.includes('group_leader') && profile?.permissions?.hasClasses === true
  )
}

// New-user gate for the school-teacher setup-progress onboarding surface
// (welcome carousel, setup checklist, sidebar progress ring). The backend
// stamps profile.createdAt when the profile document is first created;
// existing users' profiles predate the field and have no value, so the
// whole surface stays hidden for them.
export const isNewUserProfile = (profile) => {
  return Boolean(profile?.createdAt);
};

export const canAccessTasks = (profile) => {
  return (profile?.permissions?.tasks === 'UNLOCKED') || profile?.permissions?.studentTasks === 'UNLOCKED';
};

export const canAccessInsights = (profile) => {
  return profile?.permissions?.insights === "UNLOCKED";
};


export const canAccessSchoolWork = (profile) => {
  return profile?.permissions?.schoolWork === "UNLOCKED";
};

export const canAccessJeddAI = (profile) => {
  return profile?.permissions?.jeddai === "UNLOCKED";
};

export const canAccessSchoolWorkForStudent = (profile) => {
  return profile?.permissions?.schoolWork === "UNLOCKED";
};

export const canViewJeddAI = (profile) => {
  return profile?.permissions?.jeddai === "UNLOCKED";
};

export const canAccessMarkingTools = (profile) => {
  return profile?.permissions?.markingTools === "UNLOCKED";
};

export const canAccessJeddAIMarkingTool = (profile) => {
  return profile?.permissions?.markingTools === "UNLOCKED" && profile?.permissions?.hasClasses === true;
};


export const getSubscriptionTier = (profile) => {
  const sku = profile?.subscriptionDetails?.sku || '';
  if (sku.includes('FREE')) {
    return 'Free';
  } else if (sku.includes('PRO')) {
    return 'Pro'; 
  } else if (sku.includes('SCHOOL')) {
    return 'School';
  } else if (sku.includes('PLUS')) {
    return 'Plus';
  }
  return 'Free';
};

export const getCurrentPlan = (profile) => {
  return getSubscriptionTier(profile).toLowerCase();
};

export const getBillingCycle = (profile) => {
  const sku = profile?.subscriptionDetails?.sku || '';
  if (sku.includes('AN')) {
    return 'annually';
  }
  if (sku.includes('MO')) {
    return 'monthly';
  }
  return null;
};

export const canHaveUnlimitedCredits = (profile) => {
  return profile?.permissions?.credits?.isUnlimitedCredits === true;
};

export const getRemainingCredits = (profile) => {
  return profile?.permissions?.credits?.remainingCredits  || 0;
};

// Free-plan JeddAI allowance for self-serve students and teachers. Mirrors
// FreeCreditAllowance.MONTHLY_CREDITS in feedbacksBackend, which is the
// source of truth for the credit meter; change both together.
export const FREE_MONTHLY_CREDITS = 4;

export const getTotalCredits = (profile) => {
  return profile?.permissions?.credits?.totalCredits || FREE_MONTHLY_CREDITS;
};

export const canRequestFeedback = (profile) => {
  return getRemainingCredits(profile) > 0 || canHaveUnlimitedCredits(profile);
};

export const canViewCurrentTasks = (profile) => {
  return profile?.permissions?.tasksPermissions?.currentTasks === true ||  profile?.permissions?.studentTasksPermissions?.currentTasks === true;
};

export const canViewClosedTasks = (profile) => {
  return profile?.permissions?.tasksPermissions?.closedTasks === true || 
         profile?.permissions?.studentTasksPermissions?.closedTasks === true;
};

export const canViewFeedbackRequests = (profile) => {
  return profile?.permissions?.tasksPermissions?.feedbackRequests === true ||
         profile?.permissions?.studentTasksPermissions?.feedbackFromMe === true;
};

export const canViewSharedResponses = (profile) => {
  return profile?.permissions?.tasksPermissions?.sharedResponses === true || 
         profile?.permissions?.studentTasksPermissions?.helpAFriend === true;
};

export const canViewFeedbackHistory = (profile) => {
  return profile?.permissions?.tasksPermissions?.feedbackHistory === true || 
         profile?.permissions?.studentTasksPermissions?.modelResponses === true;
};

export const canAccessAnyTasks = (profile) => {
  return canViewCurrentTasks(profile) ||
         canViewClosedTasks(profile) ||
         canViewFeedbackRequests(profile) ||
         canViewSharedResponses(profile) ||
         canViewFeedbackHistory(profile);
};

export const canCreateTask = (profile) => {
  const role = getJeddaiRole(profile);
  return role && String(role).toUpperCase() === 'TEACHER' && profile?.permissions?.tasksPermissions?.currentTasks === true;
};

// NSW DoE compliance pilot — explicit per-environment allowlist of *student*
// test accounts blocked from the JeddAI feedback source on the Get Feedback
// modal. Teacher accounts in the NSW DoE LearnDash groups are deliberately
// EXCLUDED — the Get Feedback modal is a student-side flow, and teachers may
// need JeddAI for their own marking/preview workflows.
//
// IDs are not transferable across envs (Mongo user ids == WP user ids in our
// install, and the same numeric WP id maps to *different humans* on staging
// vs prod — e.g. id "162" is a staging test teacher but a real prod user).
//
//   staging  → 2 shashank+teststudent* accounts in LearnDash group
//              `shashank-english-advanced` (id 18191).
//              Teachers 162 (testteacher1) and 163 (testteacher2) excluded.
//   prod     → 2 doe-student* accounts in LearnDash group `Class-DoE-12A`
//              (id 62870). Teacher 67988 (Mrs Atwood) excluded.
//
// Replace with a `profile.isNSWDOE === true && isStudent` check (the
// `isNSWDOE` flag is already plumbed end-to-end via `UserService.isNSWDOE()`
// derivation from class membership; see consumers in `FeedbackMethodStep.jsx`
// and `JeddAIFeedbackTypeSelection`) once we're ready to expand beyond the
// pilot accounts.
const JEDDAI_BLOCKED_USER_IDS_BY_ENV = {
  staging: new Set(['167', '168']),
  prod: new Set(['67989', '67990']),
};

// Env detection. We can't rely solely on REACT_APP_ENV — the staging/prod
// `build:*` scripts in package.json don't actually set it at build time
// (only the local `serve:staging` / `serve:prod` scripts do). The signal
// that IS reliably set in every build is REACT_APP_API_BASE_URL, which
// carries a per-env GCP project hash:
//   staging → `leso2wocda` (build:staging, build:staging-cr)
//   prod    → `zbwgpqef5q` (build:prod-cr)
// Prefer REACT_APP_ENV when present (covers local serve scripts + future
// build scripts that set it explicitly); fall back to URL inference.
const detectEnv = () => {
  const explicit = (process.env.REACT_APP_ENV ?? '').toLowerCase();
  if (explicit === 'staging' || explicit === 'prod' || explicit === 'dev') {
    return explicit;
  }
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL ?? '';
  if (apiBaseUrl.includes('leso2wocda')) return 'staging';
  if (apiBaseUrl.includes('zbwgpqef5q')) return 'prod';
  return 'dev';
};

const getJeddAIBlockedUserIds = () => {
  return JEDDAI_BLOCKED_USER_IDS_BY_ENV[detectEnv()] ?? new Set();
};

export const canAccessJeddAIFeedback = (profile) => {
  // Backend `/users/profile` response (see UserController.UserProfile in
  // feedbacksBackend) exposes the user id as `userId`, NOT `id`. The first
  // version of this check used `profile?.id` and silently never fired — the
  // gate was a no-op in production (verified on staging: pilot user 167 still
  // saw all 3 tiles in the Get Feedback modal).
  if (profile?.userId && getJeddAIBlockedUserIds().has(String(profile.userId))) {
    return false;
  }
  const canAccessFeedbackFrom = profile?.permissions?.canAccessFeedbackFrom || [];
  return canAccessFeedbackFrom.includes('JEDDAI');
};

export const canAccessPeerFeedback = (profile) => {
  const canAccessFeedbackFrom = profile?.permissions?.canAccessFeedbackFrom || [];
  const res = canAccessFeedbackFrom.includes('PEER') 

  return res;
};

export const canAccessTeacherFeedback = (profile) => {
  const canAccessFeedbackFrom = profile?.permissions?.canAccessFeedbackFrom || [];
  return canAccessFeedbackFrom.includes('TEACHER');
};

export const canAccessExpertFeedback = (profile) => {
  const canAccessFeedbackFrom = profile?.permissions?.canAccessFeedbackFrom || [];
  return canAccessFeedbackFrom.includes('EXPERT');
};

export const hasOnlyJeddAIFeedback = (profile) => {
  const from = profile?.permissions?.canAccessFeedbackFrom || [];
  return from.every((option) => option === 'JEDDAI');
};
